import React, { useState, useEffect } from "react";
import API from "../api/api";
import { FaDownload, FaTimes, FaSpinner, FaFileImage } from "react-icons/fa";
import Swal from "sweetalert2";

// Helper to wrap text in Canvas
const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(" ");
  let line = "";
  let currentY = y;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, currentY);
      line = words[n] + " ";
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, currentY);
  return currentY;
};

// Helper to draw a star
const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius, fillStyle) => {
  let rot = (Math.PI / 2) * 3;
  let x = cx;
  let y = cy;
  let step = Math.PI / spikes;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(cx, cy - outerRadius);
  for (let i = 0; i < spikes; i++) {
    x = cx + Math.cos(rot) * outerRadius;
    y = cy + Math.sin(rot) * outerRadius;
    ctx.lineTo(x, y);
    rot += step;

    x = cx + Math.cos(rot) * innerRadius;
    y = cy + Math.sin(rot) * innerRadius;
    ctx.lineTo(x, y);
    rot += step;
  }
  ctx.lineTo(cx, cy - outerRadius);
  ctx.closePath();
  ctx.fillStyle = fillStyle;
  ctx.fill();
  ctx.restore();
};

// Helper to draw a golden trophy
const drawTrophy = (ctx, x, y, width, height) => {
  ctx.save();
  // Gold gradient
  const gold = ctx.createLinearGradient(x - width / 2, y, x + width / 2, y);
  gold.addColorStop(0, "#bf953f");
  gold.addColorStop(0.25, "#fcf6ba");
  gold.addColorStop(0.5, "#b38728");
  gold.addColorStop(0.75, "#fbf5b7");
  gold.addColorStop(1, "#aa771c");

  ctx.fillStyle = gold;
  ctx.shadowColor = "rgba(0, 0, 0, 0.4)";
  ctx.shadowBlur = 10;

  // Base / Plinth
  ctx.beginPath();
  ctx.moveTo(x - width * 0.35, y + height);
  ctx.lineTo(x + width * 0.35, y + height);
  ctx.lineTo(x + width * 0.28, y + height - 25);
  ctx.lineTo(x - width * 0.28, y + height - 25);
  ctx.closePath();
  ctx.fill();

  // Plinth text panel (Black plate)
  ctx.fillStyle = "#1e1e1e";
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.roundRect(x - width * 0.23, y + height - 21, width * 0.46, 17, 3);
  ctx.fill();

  // Gold text on plinth
  ctx.fillStyle = "#fcf6ba";
  ctx.font = "8px 'Montserrat', sans-serif";
  ctx.textAlign = "center";
  ctx.fillText("EXCELLENCE • INSPIRATION • INCLUSION", x, y + height - 10);

  // Plinth top tier
  ctx.fillStyle = gold;
  ctx.beginPath();
  ctx.moveTo(x - width * 0.25, y + height - 25);
  ctx.lineTo(x + width * 0.25, y + height - 25);
  ctx.lineTo(x + width * 0.2, y + height - 40);
  ctx.lineTo(x - width * 0.2, y + height - 40);
  ctx.closePath();
  ctx.fill();

  // Stem
  ctx.beginPath();
  ctx.moveTo(x - width * 0.07, y + height - 40);
  ctx.lineTo(x + width * 0.07, y + height - 40);
  ctx.lineTo(x + width * 0.07, y + height - 85);
  ctx.lineTo(x - width * 0.07, y + height - 85);
  ctx.closePath();
  ctx.fill();

  // Cup bowl
  ctx.beginPath();
  ctx.moveTo(x - width * 0.22, y + height - 85);
  ctx.lineTo(x + width * 0.22, y + height - 85);
  ctx.quadraticCurveTo(x + width * 0.28, y + height - 160, x + width * 0.24, y + height - 180);
  ctx.lineTo(x - width * 0.24, y + height - 180);
  ctx.quadraticCurveTo(x - width * 0.28, y + height - 160, x - width * 0.22, y + height - 85);
  ctx.closePath();
  ctx.fill();

  // Cup Rim / Lid
  ctx.beginPath();
  ctx.ellipse(x, y + height - 180, width * 0.24, 12, 0, 0, Math.PI * 2);
  ctx.fill();

  // Handles
  ctx.strokeStyle = gold;
  ctx.lineWidth = 10;
  ctx.lineCap = "round";
  
  // Left Handle
  ctx.beginPath();
  ctx.arc(x - width * 0.22, y + height - 135, 30, Math.PI * 0.6, Math.PI * 1.6);
  ctx.stroke();

  // Right Handle
  ctx.beginPath();
  ctx.arc(x + width * 0.22, y + height - 135, 30, Math.PI * 1.4, Math.PI * 0.4);
  ctx.stroke();

  // Star on top of the cup
  drawStar(ctx, x, y + height - 205, 5, 25, 11, gold);

  ctx.restore();
};

const ExcellenceAwardsPoster = ({ show, onClose, onDownloadComplete }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const AWARD_CATEGORIES = [
    "Best Para Coach",
    "Best Junior Para Sportsman",
    "Best Junior Para Sportswoman",
    "Best Sub-Junior Para Sportsman",
    "Best Sub-Junior Para Sportswoman",
    "Best Senior Para Sportsman",
    "Best Senior Para Sportswoman",
    "Best International Para Athlete",
    "Best National Para Athlete",
    "Best Emerging Para Athlete",
    "Best Para Team",
    "Best District Para Sports Association",
    "Best Para Sports NGO / Organization",
    "Best Technical Official / Referee",
    "Best Classifier",
    "Best Support Staff (Physiotherapist, Trainer, Volunteer, Team Manager, etc.)",
    "Lifetime Achievement Award for Outstanding Contribution to Para Sports",
    "Special Jury Award for Exceptional Service to Para Sports"
  ];

  const fetchActiveSettings = async () => {
    try {
      setLoading(true);
      const res = await API.get("/excellence-awards/active");
      setData(res.settings || {
        year: new Date().getFullYear().toString(),
        deadline_date: new Date().getFullYear() + "-08-15",
        ceremony_date: new Date().getFullYear() + "-09-05",
        venue: "Visakhapatnam, Andhra Pradesh",
        contact_email: "apparasports@gmail.com",
        contact_phone: "9390131777",
        contact_name: "V. Ramaswamy"
      });
    } catch (err) {
      console.error("Failed to load awards settings:", err);
      Swal.fire("Error", "Could not fetch dynamic poster settings.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchActiveSettings();
    }
  }, [show]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const options = { day: "numeric", month: "long", year: "numeric" };
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString("en-US", options).toUpperCase();
  };

  const handleDownload = async (type = "detailed") => {
    if (!data) return;
    setLoading(true);

    try {
      // Create Canvas
      const canvas = document.createElement("canvas");
      canvas.width = 1200;
      canvas.height = 1700;
      const ctx = canvas.getContext("2d");

      // 1. Draw Navy Gradient Background
      const bgGrad = ctx.createLinearGradient(0, 0, 0, 1700);
      bgGrad.addColorStop(0, "#081026");
      bgGrad.addColorStop(0.5, "#0d1f42");
      bgGrad.addColorStop(1, "#030611");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, 1200, 1700);

      // 2. Draw dual gold border
      ctx.strokeStyle = "#bf953f";
      ctx.lineWidth = 5;
      ctx.strokeRect(15, 15, 1170, 1670);

      ctx.strokeStyle = "#fcf6ba";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(25, 25, 1150, 1650);

      // 3. Draw Corner Stars
      const corners = [
        [25, 25],
        [1175, 25],
        [25, 1675],
        [1175, 1675]
      ];
      corners.forEach(([cx, cy]) => {
        drawStar(ctx, cx, cy, 4, 8, 3, "#fcf6ba");
      });

      // 4. Preload Logos & QR Code (high res 800x800 for clean print details)
      const origin = window.location.origin;
      const saapSrc = `${origin}/images/saaplogo.png`;
      const parasportsSrc = `${origin}/images/parasports.png`;
      const pciSrc = `${origin}/images/pcilogo.png`;
      
      const redirectUrl = "http://apparasports.com/excellence-awards#apply-form";
      const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=${encodeURIComponent(redirectUrl)}`;

      const loadImage = (src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve(img);
          img.onerror = () => {
            console.warn("Failed to load asset: " + src);
            resolve(null);
          };
          img.src = src;
        });
      };

      const [saapLogo, paraLogo, pciLogo, qrCode] = await Promise.all([
        loadImage(saapSrc),
        loadImage(parasportsSrc),
        loadImage(pciSrc),
        loadImage(qrSrc)
      ]);

      // 5. Draw India Flag at top right (PCI logo container)
      ctx.fillStyle = "#FF9933"; // Saffron
      ctx.fillRect(1010, 35, 80, 7);
      ctx.fillStyle = "#FFFFFF"; // White
      ctx.fillRect(1010, 42, 80, 7);
      ctx.fillStyle = "#128807"; // Green
      ctx.fillRect(1010, 49, 80, 7);
      // Blue chakra dot
      ctx.fillStyle = "#000080";
      ctx.beginPath();
      ctx.arc(1050, 45.5, 2.5, 0, Math.PI * 2);
      ctx.fill();

      // 6. Draw Logos
      // Sports Authority logo (Left)
      if (saapLogo) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(150, 100, 45, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.clip();
        ctx.drawImage(saapLogo, 105, 55, 90, 90);
        ctx.restore();
      }
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 11px 'Montserrat', sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("SPORTS AUTHORITY OF", 150, 165);
      ctx.fillText("ANDHRA PRADESH", 150, 178);

      // Para Sports Association logo (Center)
      if (paraLogo) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(600, 100, 52, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.clip();
        ctx.drawImage(paraLogo, 548, 48, 104, 104);
        ctx.restore();
      }
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 13px 'Montserrat', sans-serif";
      ctx.fillText("PARA SPORTS ASSOCIATION", 600, 170);
      ctx.fillText("ANDHRA PRADESH", 600, 185);

      // PCI Logo (Right)
      if (pciLogo) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(1050, 100, 45, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.clip();
        ctx.drawImage(pciLogo, 1005, 55, 90, 90);
        ctx.restore();
      }
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 11px 'Montserrat', sans-serif";
      ctx.fillText("PARALYMPIC COMMITTEE", 1050, 165);
      ctx.fillText("INDIA", 1050, 178);

      // Render shared text elements based on selected poster type
      if (type === "detailed") {
        // ==========================================
        // TYPE A: DETAILED PROMOTIONAL POSTER
        // ==========================================

        // Golden Stars (under logos)
        drawStar(ctx, 600, 220, 5, 14, 6, "#bf953f");
        drawStar(ctx, 560, 222, 5, 10, 4, "#bf953f");
        drawStar(ctx, 640, 222, 5, 10, 4, "#bf953f");

        // Main Titles
        ctx.textAlign = "left";
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 68px 'Montserrat', sans-serif";
        ctx.fillText("NATIONAL", 75, 315);
        ctx.fillText("SPORTS DAY,", 75, 385);

        // Title - Para Sports Excellence Awards (Golden Gradient)
        const textGrad = ctx.createLinearGradient(75, 0, 600, 0);
        textGrad.addColorStop(0, "#bf953f");
        textGrad.addColorStop(0.5, "#fcf6ba");
        textGrad.addColorStop(1, "#b38728");
        ctx.fillStyle = textGrad;
        ctx.font = "800 42px 'Montserrat', sans-serif";
        ctx.fillText("PARA SPORTS", 75, 445);
        ctx.fillText(`EXCELLENCE AWARDS - ${data.year}`, 75, 495);

        // Wheat/laurel wreaths look (laurel sub-line)
        ctx.fillStyle = "#fcf6ba";
        ctx.font = "italic 600 20px 'Georgia', serif";
        ctx.fillText("Recognising Excellence  •  Inspiring Inclusion  •  Empowering Champions", 75, 545);

        // Draw Trophy
        drawTrophy(ctx, 980, 250, 230, 310);

        // Call For Applications Banner
        ctx.save();
        const ribGrad = ctx.createLinearGradient(75, 0, 1125, 0);
        ribGrad.addColorStop(0, "#b38728");
        ribGrad.addColorStop(0.5, "#fcf6ba");
        ribGrad.addColorStop(1, "#bf953f");
        ctx.fillStyle = ribGrad;
        ctx.beginPath();
        ctx.roundRect(75, 580, 1050, 48, 6);
        ctx.fill();

        ctx.fillStyle = "#0c152a";
        ctx.font = "900 24px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("CALL FOR APPLICATIONS", 600, 613);
        ctx.restore();

        // Intro Paragraph
        ctx.fillStyle = "#cbd5e1";
        ctx.font = "500 16px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        const introText = `The Para Sports Association of Andhra Pradesh (PSAAP) is pleased to invite applications for the National Sports Day Excellence Awards - ${data.year}, to be presented during a special Awards Ceremony on ${formatDate(data.ceremony_date)} in ${data.venue}. These prestigious awards aim to recognize and honour outstanding achievements, dedication, and exceptional contributions towards the promotion and development of para sports.`;
        wrapText(ctx, introText, 600, 665, 1040, 24);

        // Award Categories Section
        ctx.save();
        const catRib = ctx.createLinearGradient(400, 0, 800, 0);
        catRib.addColorStop(0, "#0d2654");
        catRib.addColorStop(0.5, "#1e3a8a");
        catRib.addColorStop(1, "#0d2654");
        ctx.fillStyle = catRib;
        ctx.strokeStyle = "#bf953f";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(380, 755, 440, 36, 18);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#fcf6ba";
        ctx.font = "800 16px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("AWARD CATEGORIES", 600, 779);
        ctx.restore();

        // List Categories (3 columns of 6 items each)
        const colWidth = 330;
        const startX = 75;
        const colGap = 40;
        const startY = 825;
        const rowHeight = 48;

        ctx.textAlign = "left";
        ctx.font = "700 12px 'Montserrat', sans-serif";

        AWARD_CATEGORIES.forEach((cat, index) => {
          const col = index % 3;
          const row = Math.floor(index / 3);
          const x = startX + col * (colWidth + colGap);
          const y = startY + row * rowHeight;

          // Draw index badge
          ctx.fillStyle = "#bf953f";
          ctx.beginPath();
          ctx.arc(x + 12, y + 10, 11, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = "#0c152a";
          ctx.font = "900 10.5px 'Montserrat', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(String(index + 1), x + 12, y + 13.5);

          // Draw Category Text
          ctx.fillStyle = "#ffffff";
          ctx.font = "bold 11.5px 'Montserrat', sans-serif";
          ctx.textAlign = "left";
          wrapText(ctx, cat, x + 30, y + 5, colWidth - 32, 14);
        });

        // Lower Grid: Timeline & QR Code Box
        const lowerY = 1075;
        const lowerHeight = 385;

        // Left Box: Timeline & Dates
        ctx.save();
        ctx.fillStyle = "rgba(15, 23, 42, 0.65)";
        ctx.strokeStyle = "rgba(191, 149, 63, 0.4)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(75, lowerY, 620, lowerHeight, 16);
        ctx.fill();
        ctx.stroke();

        // Title badge
        ctx.fillStyle = "#dc2626"; // Crimson header
        ctx.beginPath();
        ctx.roundRect(100, lowerY - 15, 220, 30, 6);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 13px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("IMPORTANT TIMELINE", 210, lowerY + 4);

        // Contents
        ctx.textAlign = "left";
        
        // Last date
        ctx.fillStyle = "#f87171";
        ctx.font = "900 12px 'Montserrat', sans-serif";
        ctx.fillText("LAST DATE FOR SUBMISSION OF APPLICATIONS", 110, lowerY + 55);
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 24px 'Montserrat', sans-serif";
        ctx.fillText(formatDate(data.deadline_date), 110, lowerY + 90);

        // Divider line 1
        ctx.strokeStyle = "rgba(255, 255, 255, 0.1)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(110, lowerY + 130);
        ctx.lineTo(650, lowerY + 130);
        ctx.stroke();

        // Ceremony
        ctx.fillStyle = "#bf953f";
        ctx.font = "900 12px 'Montserrat', sans-serif";
        ctx.fillText("AWARDS CEREMONY DATE", 110, lowerY + 170);
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 24px 'Montserrat', sans-serif";
        ctx.fillText(formatDate(data.ceremony_date), 110, lowerY + 205);

        // Divider line 2
        ctx.beginPath();
        ctx.moveTo(110, lowerY + 245);
        ctx.lineTo(650, lowerY + 245);
        ctx.stroke();

        // Venue
        ctx.fillStyle = "#bf953f";
        ctx.font = "900 12px 'Montserrat', sans-serif";
        ctx.fillText("AWARDS CEREMONY VENUE", 110, lowerY + 285);
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 16px 'Montserrat', sans-serif";
        wrapText(ctx, data.venue.toUpperCase(), 110, lowerY + 310, 540, 20);
        ctx.restore();

        // Right Box: QR Code Card
        ctx.save();
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#bf953f";
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.roundRect(725, lowerY, 400, lowerHeight, 16);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "#0c152a";
        ctx.font = "900 15px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("SCAN TO APPLY ONLINE", 925, lowerY + 30);

        // Draw QR Code (320x320)
        const qrSize = 320;
        const qrX = 925 - qrSize / 2;
        const qrY = lowerY + 40;

        if (qrCode) {
          ctx.drawImage(qrCode, qrX, qrY, qrSize, qrSize);
        } else {
          ctx.fillStyle = "#0c152a";
          ctx.font = "bold 13px 'Montserrat', sans-serif";
          ctx.fillText("[QR Code Image]", 925, lowerY + 180);
        }

        ctx.fillStyle = "#475569";
        ctx.font = "bold 11px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("apparasports.com/excellence-awards", 925, lowerY + 372);
        ctx.restore();

      } else {
        // ==========================================
        // TYPE B: VERY VERY BIG QR SCAN FLYER
        // ==========================================

        ctx.textAlign = "center";
        
        // Large Titles Centered
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 38px 'Montserrat', sans-serif";
        ctx.fillText("NATIONAL SPORTS DAY INITIATIVE", 600, 235);

        const textGrad = ctx.createLinearGradient(200, 0, 1000, 0);
        textGrad.addColorStop(0, "#bf953f");
        textGrad.addColorStop(0.5, "#fcf6ba");
        textGrad.addColorStop(1, "#b38728");
        ctx.fillStyle = textGrad;
        ctx.font = "800 44px 'Montserrat', sans-serif";
        ctx.fillText(`PARA SPORTS EXCELLENCE AWARDS - ${data.year}`, 600, 290);

        ctx.fillStyle = "#fcf6ba";
        ctx.font = "italic 600 20px 'Georgia', serif";
        ctx.fillText("Empowering Champions  •  Recognising Excellence  •  Inspiring Inclusion", 600, 335);

        // humongous QR Container Card
        const cardX = 225;
        const cardY = 380;
        const cardSize = 750;

        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#bf953f";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.roundRect(cardX, cardY, cardSize, cardSize, 24);
        ctx.fill();
        ctx.stroke();

        // Draw massive QR code inside card
        if (qrCode) {
          ctx.drawImage(qrCode, cardX + 40, cardY + 40, cardSize - 80, cardSize - 80);
        } else {
          ctx.fillStyle = "#0c152a";
          ctx.font = "bold 24px 'Montserrat', sans-serif";
          ctx.fillText("[QR Code Image]", 600, cardY + 360);
          ctx.font = "normal 18px 'Montserrat', sans-serif";
          ctx.fillText("apparasports.com/excellence-awards", 600, cardY + 400);
        }

        // Call to action text below QR card
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 32px 'Montserrat', sans-serif";
        ctx.fillText("SCAN THE QR CODE TO APPLY ONLINE", 600, 1195);

        ctx.fillStyle = "#fcf6ba";
        ctx.font = "800 20px 'Montserrat', sans-serif";
        ctx.fillText("http://apparasports.com/excellence-awards", 600, 1240);

        // Timeline highlighting bar
        ctx.save();
        ctx.fillStyle = "rgba(191, 149, 63, 0.08)";
        ctx.strokeStyle = "rgba(191, 149, 63, 0.3)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(150, 1285, 900, 140, 12);
        ctx.fill();
        ctx.stroke();

        ctx.textAlign = "left";
        ctx.fillStyle = "#f87171";
        ctx.font = "900 13px 'Montserrat', sans-serif";
        ctx.fillText("LAST DATE TO SUBMIT APPLICATIONS: " + formatDate(data.deadline_date), 360, 1345);
        
        ctx.fillStyle = "#ffffff";
        ctx.font = "900 13px 'Montserrat', sans-serif";
        ctx.fillText("CEREMONY VENUE: " + data.venue.toUpperCase(), 360, 1385);

        // Date Year badge
        ctx.fillStyle = "#dc2626";
        ctx.beginPath();
        ctx.roundRect(200, 1310, 90, 90, 8);
        ctx.fill();
        ctx.fillStyle = "#ffffff";
        ctx.font = "800 32px 'Montserrat', sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(data.year, 245, 1365);
        ctx.restore();
      }

      // 12. Bottom Quote & Secretary Info (Shared)
      const quoteY = 1485;
      
      // Divider
      ctx.strokeStyle = "rgba(191, 149, 63, 0.4)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(75, quoteY);
      ctx.lineTo(1125, quoteY);
      ctx.stroke();

      // Quote
      ctx.fillStyle = "#fcf6ba";
      ctx.font = "italic 700 15px 'Georgia', serif";
      ctx.textAlign = "center";
      ctx.fillText("“ EVERY ACHIEVEMENT DESERVES RECOGNITION. EVERY CHAMPION DESERVES RESPECT. ”", 600, quoteY + 28);

      // Mail Info
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 12px 'Montserrat', sans-serif";
      ctx.fillText("SEND YOUR APPLICATIONS TO:  " + data.contact_email.toLowerCase(), 600, quoteY + 55);

      // Contact bar (Secretary details)
      ctx.save();
      ctx.fillStyle = "rgba(191, 149, 63, 0.08)";
      ctx.beginPath();
      ctx.roundRect(75, quoteY + 75, 1050, 52, 6);
      ctx.fill();
      ctx.strokeStyle = "rgba(191, 149, 63, 0.3)";
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.fillStyle = "#fcf6ba";
      ctx.font = "bold 11px 'Montserrat', sans-serif";
      ctx.fillText("FOR APPLICATION FORMS AND FURTHER DETAILS, PLEASE CONTACT:", 600, quoteY + 93);
      
      ctx.fillStyle = "#ffffff";
      ctx.font = "800 14px 'Montserrat', sans-serif";
      ctx.fillText(`${data.contact_name.toUpperCase()} (Secretary, PSAAP)   |   Phone: +91 ${data.contact_phone}`, 600, quoteY + 114);
      ctx.restore();

      // Download trigger
      const imgUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = imgUrl;
      link.download = type === "qr_flyer" 
        ? `Para_Sports_Excellence_Awards_QR_Flyer_${data.year}.png` 
        : `Para_Sports_Excellence_Awards_${data.year}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await Swal.fire({
        icon: "success",
        title: type === "qr_flyer" ? "Flyer Downloaded!" : "Downloaded!",
        text: type === "qr_flyer" 
          ? `Awards QR Flyer for year ${data.year} downloaded successfully!`
          : `Awards Promotional Poster for year ${data.year} downloaded successfully!`,
        confirmButtonColor: "#0f3373"
      });

      if (onDownloadComplete) onDownloadComplete();
      onClose();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to generate and download image.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-lg shadow-2xl flex flex-col overflow-hidden text-left animate-in fade-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-950 text-white">
          <div className="flex items-center gap-2">
            <span className="text-xl text-amber-500">🏆</span>
            <div>
              <h3 className="font-extrabold text-sm">Awards Poster Downloader</h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Dynamically generates official application poster</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-all"
          >
            <FaTimes size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 flex-grow max-h-[70vh] overflow-y-auto">
          {loading && !data ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400 space-y-3">
              <FaSpinner className="animate-spin text-amber-500 text-3xl" />
              <p className="text-xs font-semibold">Fetching latest settings...</p>
            </div>
          ) : data ? (
            <div className="space-y-4">
              <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 space-y-3">
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400 font-medium">Award Year:</span>
                  <span className="font-bold text-amber-400 text-sm">{data.year}</span>
                </div>
                
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400 font-medium">Deadline Date:</span>
                  <span className="font-bold text-slate-200">{formatDate(data.deadline_date)}</span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400 font-medium">Ceremony Date:</span>
                  <span className="font-bold text-slate-200">{formatDate(data.ceremony_date)}</span>
                </div>

                <div className="flex justify-between items-start text-xs">
                  <span className="text-slate-400 font-medium pt-0.5">Venue:</span>
                  <span className="font-bold text-slate-200 text-right max-w-[200px]">{data.venue}</span>
                </div>

                <div className="flex justify-between items-center text-xs border-t border-slate-800/80 pt-3">
                  <span className="text-slate-400 font-medium">Secretary:</span>
                  <span className="font-bold text-slate-200">{data.contact_name}</span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400 font-medium">Contact Phone:</span>
                  <span className="font-bold text-slate-200">{data.contact_phone}</span>
                </div>

                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-400 font-medium">Contact Email:</span>
                  <span className="font-bold text-slate-200 text-right truncate max-w-[220px]">{data.contact_email}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl text-[11px] leading-relaxed flex gap-2.5">
                <span className="text-sm">ℹ️</span>
                <p>
                  You can choose to download the <strong>Detailed Poster</strong> (containing categories list and timeline) or the <strong>QR Code Flyer</strong> (featuring a very very big scan-to-apply QR code ideal for printing).
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-400 text-xs">
              Failed to load template data.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950 flex flex-wrap justify-between items-center gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-700 hover:bg-slate-900 text-slate-300 rounded-xl text-xs font-bold transition-all"
          >
            Close
          </button>
          
          <div className="flex gap-2">
            <button
              onClick={() => handleDownload("qr_flyer")}
              disabled={loading || !data}
              className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-amber-400 border border-amber-500/30 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 transition-all"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaFileImage />}
              Download QR Flyer
            </button>
            
            <button
              onClick={() => handleDownload("detailed")}
              disabled={loading || !data}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-xl text-xs font-black inline-flex items-center gap-1.5 transition-all shadow-md"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
              Download Detailed Poster
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ExcellenceAwardsPoster;
